"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Accordion = Accordion;
exports.default = Collapsible;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _summary = _interopRequireDefault(require("@ux/summary"));
var _reveal = _interopRequireDefault(require("@ux/reveal"));
var _siblingSet = _interopRequireDefault(require("@ux/sibling-set"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/use-controllable-state"));
var _legacy = require("./legacy");
const _excluded = ["children", "className", "id", "open", "title"],
  _excluded2 = ["children", "className", "defaultOpen", "emphasis", "icon", "id", "onChange", "onComplete", "open", "summary", "target", "title"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const UX_COLLAPSIBLE_CLASSNAME = 'ux-collapse';
const CollapsibleContext = /*#__PURE__*/(0, _react.createContext)(null);

/**
 * Creates an <Accordion/> component
 *
 * @param {Object} props - Configuration object
 * @param {React.ReactNode} props.children - <Collapsible/> components
 * @param {String} [props.className] - Optional classname for positioning
 * @param {String} props.id - Unique identifier
 * @param {String} [props.open] - Id of the first open <Collapsible/> component
 * @param {React.ReactNode} [props.title] - Optional title
 * @returns {React.Component} - Accordion Context Component
 */
function Accordion(props) {
  const {
      children,
      className,
      id,
      open: defaultOpenId,
      title
    } = props,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);
  const [openId, setOpenId] = (0, _react.useState)(defaultOpenId);
  // https://react.dev/reference/react/useContext#optimizing-re-renders-when-passing-objects-and-functions
  const contextValue = (0, _react.useMemo)(function memoizedValue() {
    return {
      openId,
      setOpenId
    };
  }, [openId, setOpenId]);
  return /*#__PURE__*/_react.default.createElement(CollapsibleContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/_react.default.createElement(_siblingSet.default, (0, _extends2.default)({}, rest, {
    label: title,
    stretch: true,
    orientation: "vertical",
    id: id,
    className: (0, _classnames.default)(`${UX_COLLAPSIBLE_CLASSNAME}-accordion`, className)
  }), children));
}
Accordion.propTypes = {
  /** <Collapsible/> components */
  children: _propTypes.default.node.isRequired,
  /** Optional classname for positioning */
  className: _propTypes.default.string,
  /** Unique identifier */
  id: _propTypes.default.string.isRequired,
  /** Id of the first open <Collapsible/> component */
  open: _propTypes.default.string,
  /** Optional title */
  title: _propTypes.default.node
};
Accordion.displayName = 'Accordion';

/**
 * Creates a <Collapsible/> component
 *
 * @param {Object} props - Configuration object
 * @param {React.ReactNode} props.children - Content for the reveal
 * @param {String} [props.className] - Optional classname for positioning
 * @param {Boolean} [props.defaultOpen] - Sets the reveal as open
 * @param {String} [props.emphasis] - Provides feedback
 * @param {React.ReactElement} [props.icon] - Optional @ux/icon component
 * @param {String} props.id - Unique identifier
 * @param {Function} [props.onChange] - Callback function fires when open/closed
 * @param {Function} [props.onComplete] - Callback function fires when animation is complete
 * @param {Boolean} [props.open] - State of the reveal
 * @param {React.ReactNode} [props.summary] - Additional info for summary, under the title
 * @param {String} [props.target] - Interactive area of the summary (default: 'root', use 'button' to only target button)
 * @param {React.ReactNode} props.title - Title of the summary
 * @returns {React.Component} - A <Collapsible/> component
 */
function Collapsible(_ref) {
  let {
      children,
      className,
      defaultOpen: defaultProp,
      emphasis,
      icon,
      id,
      onChange: onControllableChange = Function.prototype,
      onComplete,
      open: controllableProp,
      summary,
      target,
      title
    } = _ref,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded2);
  delete rest.style;
  const transformedProps = (0, _legacy.transform)(_objectSpread({
    icon,
    onChange: onControllableChange
  }, rest));
  const contextValue = (0, _react.useContext)(CollapsibleContext);
  const [tabIndex, setTabIndex] = (0, _react.useState)(null);
  const [open, onChange] = (0, _useControllableState.default)({
    prop: contextValue !== null && contextValue !== void 0 && contextValue.openId ? contextValue.openId === id : controllableProp,
    defaultProp,
    onChange: transformedProps.onChange
  });
  const onClick = (0, _react.useCallback)(function _onClick(event) {
    if (contextValue !== null && contextValue !== void 0 && contextValue.setOpenId) {
      // We explicity use __UNSET__ to avoid switching between uncontrolled/controlled
      contextValue.setOpenId(id !== contextValue.openId ? id : '__UNSET__');
    }
    onChange(!open, {
      event
    });
  }, [open, contextValue, id, onChange]);

  // Remove focus target if JavaScript has loaded, provided to <Summary/>
  (0, _react.useEffect)(() => setTabIndex(-1), []);

  // Callback will only occur after successful JS mount, intercepted by <Summary/>
  const preventDefault = {
    onClick: ev => ev.preventDefault(),
    onKeyDown: ev => [' ', 'Enter'].includes(ev.key) && ev.preventDefault()
  };
  return /*#__PURE__*/_react.default.createElement("details", {
    "data-version": "2400",
    open: true,
    className: (0, _classnames.default)(`${UX_COLLAPSIBLE_CLASSNAME}-shell`, className),
    id: id
  }, /*#__PURE__*/_react.default.createElement("summary", (0, _extends2.default)({
    tabIndex: tabIndex
  }, preventDefault), /*#__PURE__*/_react.default.createElement(_summary.default, {
    className: `${UX_COLLAPSIBLE_CLASSNAME}-summary`,
    target: target,
    emphasis: emphasis,
    icon: transformedProps.icon,
    title: title,
    id: `${id}-summary`,
    onClick: onClick,
    rotation: open ? '-180deg' : 0,
    "aria-expanded": open,
    "aria-controls": `${id}-reveal`
  }, summary)), /*#__PURE__*/_react.default.createElement(_reveal.default, {
    className: `${UX_COLLAPSIBLE_CLASSNAME}-reveal`,
    id: `${id}-reveal`,
    open: open,
    onComplete: onComplete
  }, children));
}
Collapsible.propTypes = {
  /** Content for the reveal */
  children: _propTypes.default.node.isRequired,
  /** Optional classname for positioning */
  className: _propTypes.default.string,
  /** Sets the reveal as open by default */
  defaultOpen: _propTypes.default.bool,
  /** Provides feedback and icon if not provided */
  emphasis: _propTypes.default.oneOf(Object.keys(_summary.default.ACCESSORIES)),
  /** Optional @ux/icon component */
  icon: _propTypes.default.node,
  /** Unique identifier */
  id: _propTypes.default.string.isRequired,
  /** Callback function fires when open/closed */
  onChange: _propTypes.default.func,
  /** Callback function fires when animation is complete */
  onComplete: _propTypes.default.func,
  /** State of the reveal */
  open: _propTypes.default.bool,
  /** Additional info for summary, under the title */
  summary: _propTypes.default.node,
  /** Interactive area of the summary (default: 'root', use 'button' to only target button) */
  target: _propTypes.default.oneOf(['root', 'button']),
  /** Title of the summary */
  title: _propTypes.default.node.isRequired
};
Collapsible.displayName = 'Collapsible';